import { action, computed, observable } from 'mobx'
import { $RootStore } from '../../src/stores/RootStore'

export const SETUP_TABS = [
  { id: 'todo', label: 'Todo' },
  { id: 'completed', label: 'Completed' },
]
export type SelectedTab = typeof SETUP_TABS[number]['id']

export class BaseSetupScreenVM {
  store: $RootStore
  @observable accessor openTaskId: string | undefined = undefined
  @observable accessor selectedTab: SelectedTab = 'todo'
  @observable accessor openAccordions: Record<string, boolean> = {}

  constructor(store: $RootStore) {
    this.store = store
  }

  @action setOpenTaskId = (taskId: string | undefined) => {
    this.openTaskId = taskId
    if (taskId) {
      this.openAccordions = { ...this.openAccordions, [taskId]: true }
    }
  }

  @action setSelectedTab = (tab: string) => {
    this.selectedTab = tab as SelectedTab
  }

  @action setSelectedTabByIndex = (index: number) => {
    this.selectedTab = SETUP_TABS[index].id
  }

  @action toggleAccordion = (groupId: string) => {
    this.openAccordions = { ...this.openAccordions, [groupId]: !this.openAccordions[groupId] }
  }

  get selectedTabIndex() {
    return SETUP_TABS.findIndex(tab => tab.id === this.selectedTab)
  }

  get setupStore() {
    return this.store.setupStore
  }

  get completedStepIds() {
    return this.setupStore.completedStepIds
  }

  @computed get groups() {
    const { setupTasksWithStatus, skippedStepIdsForPlatform } = this.setupStore
    return setupTasksWithStatus.map(task => ({
      id: task.taskId,
      name: task.title,
      steps: this.setupStore.getTaskSteps(task.taskId)
        .filter(step => !skippedStepIdsForPlatform.includes(step.id)),
    }))
  }

  @computed get todoGroups() {
    return this.groups
      .map(g => ({
        ...g,
        items: g.steps.filter(step => !this.completedStepIds.includes(step.id)),
      }))
      .filter(g => g.items.length > 0)
  }

  @computed get completedGroups() {
    return this.groups
      .map(g => ({
        ...g,
        items: g.steps.filter(step => this.completedStepIds.includes(step.id)),
      }))
      .filter(g => g.items.length > 0)
  }

  @computed get tasksData() {
    const { setupTasksWithStatus } = this.store.setupStore

    return setupTasksWithStatus.map(task => {
      const locked = !task.dependsOnTasks.every(taskId => {
        const depTask = setupTasksWithStatus.find(t => t.taskId === taskId)
        return depTask?.status === 'COMPLETED'
      })

      return {
        ...task,
        locked,
      }
    })
  }

  isStepCompleted = (stepId: string) => {
    return this.completedStepIds.includes(stepId)
  }

  getStepOnPress = (stepId: string) => {
    return this.setupStore.getStepOnPress(stepId, this.isStepCompleted(stepId))
  }

  get isDev() {
    return this.store.config.name === 'development' || this.store.config.name === 'staging'
  }

  resetStep = (stepId: string) => {
    this.setupStore.resetStep(stepId)
  }
}
